import { v4 as uuid } from 'uuid'

import { posthogManager } from './manager'

export const billsTabs = [
  'drafts',
  'needs_approval',
  'scheduled',
  'processing',
  'payment_history'
] as const
export type BillsTab = (typeof billsTabs)[number]
export type BillsTraffic =
  | 'partner_self_service'
  | 'internal'
  | 'test'
  | 'impersonated'
  | 'assisted'
  | 'unknown'
export type BillsEnvironment = 'production' | 'staging' | 'development'
type Properties = Record<string, unknown>
export interface BillsContext {
  sessionId?: string
  screenId: 'bills_list'
  screenVisitId: string
  originScreenVisitId: string
  userId: string
  accountId: string
  partnerId: string
  tab: BillsTab
  environment: BillsEnvironment
  trafficClass: BillsTraffic
}
export type BillsAttempt = BillsContext & {
  actionAttemptId: string
  operationId: string
  inputMethod: 'pointer' | 'keyboard' | 'drop'
  startedAt: number
  requestId?: string
}
export type BillsLoad = BillsContext &
  Partial<
    Pick<BillsAttempt, 'actionAttemptId' | 'operationId' | 'inputMethod'>
  > & {
    loadAttemptId: string
    queryRevisionId: string
    startedAt: number
    requestId?: string
    networkCompleted?: boolean
    claimed?: boolean
  }

// This dictionary is also the payload allowlist. Values are checked below;
// callers cannot accidentally add search text, documents or provider errors.
export const billsEventFields = {
  screenViewed: 'entrySource visitType loadDurationMs availableTabs',
  tabChanged: 'previousTab tab loadAttemptId',
  tableStateChanged:
    'state previousState loadAttemptId queryRevisionId requestId source rowCount totalCount hasSearch activeFilterTypes durationMs errorCode errorId countsComplete',
  searchCommitted: 'queryRevisionId loadAttemptId hasQuery queryLengthBucket',
  filterPanelChanged: 'filterType state reason',
  filterFieldInteracted:
    'filterType field interaction hasValue vendorId selectedVendorCount hasQuery queryLengthBucket datePreset',
  filterApplied:
    'filterType queryRevisionId loadAttemptId activeFilterTypes vendorIds selectedVendorCount datePreset hasLowerBound hasUpperBound',
  filterRemoved:
    'removalScope removedFilterTypes remainingFilterTypes queryRevisionId loadAttemptId',
  filterValidationFailed: 'filterType field reason errorId',
  vendorSearchCommitted: 'hasQuery queryLengthBucket loadAttemptId requestId',
  vendorSearchCompleted:
    'outcome resultCount durationMs loadAttemptId requestId errorCode errorId',
  organizerChanged: 'state',
  columnChanged:
    'change columnId visible previousIndex newIndex visibleColumnCount columnOrder',
  tablePreferenceSaved: 'preference outcome durationMs errorCode errorId',
  pageChanged: 'previousPage page pageSize method loadAttemptId',
  pageSizeChanged: 'previousPageSize pageSize loadAttemptId',
  selectionChanged:
    'change billId selectedBillIds selectedCount selectableCount selectionScope reason',
  eligibilityShown:
    'action billId selectedCount eligibleCount blockedCount reason presentation',
  rowMenuChanged: 'billId state availableActions',
  textExpansionChanged: 'billId field state',
  actionClicked:
    'action source billId vendorId billIds selectedCount destination',
  destinationOpened:
    'destination destinationVisitId durationMs billId vendorId billIds',
  confirmationChanged: 'action state scope billIds eligibleCount blockedCount',
  operationSubmitted:
    'operation scope billIds requestedCount requestId vendorPaymentIds',
  payDateEditorChanged: 'billId state reason',
  payDateSelected: 'billId changed direction validation requestId',
  payDateCalendarNavigated: 'billId control direction',
  newBillMenuChanged: 'state reason availableActions isImporting',
  emailCopyRequested: '',
  emailCopyCompleted: 'outcome durationMs errorCode errorId',
  uploadSelectionStarted: 'uploadBatchId selectionMethod',
  uploadSelectionCanceled: 'uploadBatchId',
  uploadFilesSelected:
    'uploadBatchId selectedCount acceptedCount rejectedCount capacityDeferredCount uploadAttemptIds',
  uploadValidationCompleted:
    'uploadBatchId uploadAttemptId fileType sizeBucket outcome reason',
  uploadDispatched:
    'uploadBatchId uploadAttemptId requestId fileType sizeBucket',
  importOutcomeViewed:
    'importId uploadAttemptId presentationScreenId outcome createdCount countsComplete',
  errorObserved:
    'operation errorCode errorId requestId billId billIds uploadAttemptId uploadBatchId',
  attemptUnresolved:
    'operation reason elapsedMs requestId billIds uploadAttemptId importId',
  supportEntryClicked: 'entryPoint destination',
  supportSearchCommitted: 'hasQuery queryLengthBucket resultCount',
  supportMessageEntryClicked: '',
  visitEnded: 'exitReason elapsedMs activeMs lastAction summaryRevision'
} as const
export type BillsEvent = keyof typeof billsEventFields

const enums: Record<string, readonly string[]> = {
  tab: billsTabs,
  previousTab: billsTabs,
  availableTabs: billsTabs,
  state: [
    'loading',
    'populated',
    'empty',
    'no_results',
    'error',
    'opened',
    'closed',
    'canceled',
    'expanded',
    'collapsed'
  ],
  previousState: ['loading', 'populated', 'empty', 'no_results', 'error'],
  source: [
    'confirmation_button',
    'cache',
    'network',
    'header',
    'row_link',
    'row_button',
    'row_menu',
    'bulk_toolbar'
  ],
  filterType: ['menu', 'vendor', 'due_date', 'amount'],
  activeFilterTypes: ['vendor', 'due_date', 'amount'],
  removedFilterTypes: ['vendor', 'due_date', 'amount'],
  remainingFilterTypes: ['vendor', 'due_date', 'amount'],
  queryLengthBucket: ['empty', '1_to_10', '11_to_30', 'over_30'],
  field: [
    'vendor_search',
    'vendor_selection',
    'date_preset',
    'date_start',
    'date_end',
    'amount_min',
    'amount_max',
    'bill_number',
    'vendor'
  ],
  interaction: ['focus', 'change', 'select', 'deselect'],
  datePreset: [
    'custom',
    'last_week',
    'last_15_days',
    'last_30_days',
    'last_6_months',
    'last_year'
  ],
  change: [
    'visibility',
    'reorder',
    'reset',
    'select_row',
    'deselect_row',
    'select_page',
    'clear',
    'automatic_clear'
  ],
  columnId: [
    'billNumber',
    'vendor',
    'origin',
    'dueDate',
    'payDate',
    'amount',
    'createdOn',
    'invoiceDate',
    'approvedBy',
    'paymentPreference',
    'estimatedArrival',
    'cancelBefore',
    'status'
  ],
  preference: ['columns', 'page_size'],
  method: ['previous', 'next', 'number', 'jump'],
  selectionScope: ['current_page'],
  presentation: ['disabled_control', 'selection_alert', 'dialog'],
  scope: ['single', 'bulk'],
  action: [
    'create_bill',
    'edit_bill',
    'review_and_fix',
    'open_bill',
    'open_vendor',
    'review_bill',
    'approval_submission',
    'approve_and_pay',
    'mark_paid_externally',
    'delete',
    'cancel_payment',
    'cancel_schedule',
    'cancel_vendor_payment',
    'change_receiving_method',
    'open_forwarding_email'
  ],
  operation: [
    'table_load',
    'search',
    'filter',
    'pagination',
    'preference',
    'approval_submission',
    'delete',
    'mark_paid_externally',
    'cancel_schedule',
    'cancel_vendor_payment',
    'pay_date',
    'navigation',
    'clipboard',
    'upload',
    'support',
    'bill_import'
  ],
  destination: [
    'bill_create',
    'bill_edit',
    'bill_details',
    'review_bill',
    'vendor_details',
    'payment_review',
    'receiving_method',
    'confirmation',
    'email_client',
    'support_widget',
    'help_center'
  ],
  direction: ['earlier', 'later', 'unchanged'],
  validation: ['valid', 'invalid'],
  control: ['previous_month', 'next_month', 'month', 'year'],
  selectionMethod: ['picker', 'drop'],
  fileType: ['pdf', 'png', 'jpeg', 'unsupported'],
  sizeBucket: ['under_1_mb', '1_to_5_mb', 'over_5_to_10_mb', 'over_10_mb'],
  removalScope: ['single', 'all'],
  outcome: [
    'results',
    'no_results',
    'failed',
    'saved',
    'success',
    'failure',
    'accepted',
    'rejected',
    'deferred',
    'created',
    'partial',
    'no_new_bills'
  ],
  reason: [
    'trigger',
    'selection',
    'dismiss',
    'cancel',
    'apply',
    'invalid_range',
    'invalid_format',
    'missing_bound',
    'unknown',
    'page_change',
    'tab_change',
    'action_completed',
    'query_change',
    'rejected_bill',
    'pay_date_invalid',
    'quickbooks_linked',
    'capture_in_flight',
    'funding_captured',
    'cancellation_unavailable',
    'permission_denied',
    'user',
    'deep_link',
    'valid',
    'unsupported_type',
    'too_large',
    'capacity',
    'timeout',
    'connection_lost'
  ],
  entrySource: ['sidebar', 'overview', 'direct', 'other_screen', 'refresh'],
  visitType: ['first', 'return'],
  entryPoint: ['widget', 'help_center'],
  exitReason: ['navigation', 'hidden', 'closed', 'unknown'],
  presentationScreenId: ['bills_list', 'ap_overview', 'other_screen'],
  errorCode: [
    'validation',
    'permission_denied',
    'state_conflict',
    'not_found',
    'quickbooks_linked',
    'capture_in_flight',
    'funding_captured',
    'cancellation_unavailable',
    'network',
    'timeout',
    'service_unavailable',
    'persistence_failed',
    'queue_failed',
    'extraction_failed',
    'unsupported_type',
    'too_large',
    'capacity',
    'clipboard_denied',
    'unknown'
  ]
}
enums.columnOrder = enums.columnId
enums.availableActions = enums.action
enums.lastAction = Object.keys(billsEventFields)
const idPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export const isBillsId = (value: unknown): value is string =>
  typeof value === 'string' && idPattern.test(value)
const commonFields = [
  'originSessionId',
  'actionAttemptId',
  'operationId',
  'requestId',
  'inputMethod'
]

export const safeBillsProperties = (
  fields: readonly string[],
  values: Properties
): Properties => {
  const result: Properties = {}
  for (const field of fields) {
    const value = values[field]
    const allowed = enums[field]
    if (allowed) {
      if (typeof value === 'string' && allowed.includes(value))
        result[field] = value
      else if (Array.isArray(value))
        result[field] = value.filter(
          v => typeof v === 'string' && allowed.includes(v)
        )
    } else if (field === 'inputMethod') {
      if (['pointer', 'keyboard', 'drop'].includes(String(value)))
        result[field] = value
    } else if (/Ids$|IDs$/.test(field)) {
      if (Array.isArray(value)) result[field] = value.filter(isBillsId)
    } else if (/Id$|ID$/.test(field)) {
      if (isBillsId(value)) result[field] = value
    } else if (typeof value === 'boolean' || value === null)
      result[field] = value
    else if (typeof value === 'number' && Number.isFinite(value) && value >= 0)
      result[field] = value
  }
  return result
}

export const billsQueryBucket = (value: string) =>
  value.length === 0
    ? 'empty'
    : value.length <= 10
    ? '1_to_10'
    : value.length <= 30
    ? '11_to_30'
    : 'over_30'
export const billsErrorCode = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'unknown'
  const e = error as {
    name?: string
    statusCode?: number
    networkError?: { statusCode?: number }
    graphQLErrors?: { extensions?: { code?: unknown } }[]
  }
  if (e.name === 'AbortError' || e.name === 'TimeoutError') return 'timeout'
  const status = e.statusCode ?? e.networkError?.statusCode
  if (status === 403 || status === 401) return 'permission_denied'
  if (status === 404) return 'not_found'
  if (status === 409) return 'state_conflict'
  if (status === 400 || status === 422) return 'validation'
  if (status && status >= 500) return 'service_unavailable'
  if (e.networkError) return 'network'
  const code = e.graphQLErrors?.[0]?.extensions?.code
  return typeof code === 'string' && enums.errorCode.includes(code)
    ? code
    : 'unknown'
}

type BillsEntrySource = 'sidebar' | 'overview' | 'other_screen'
let sidebarEntry = false
let nextEntrySource: BillsEntrySource | undefined
let navigationHandoff:
  | { target: string; attempt: BillsAttempt; properties: Properties }
  | undefined
export const rememberBillsSidebarEntry = (destination: unknown) => {
  sidebarEntry =
    typeof destination === 'string' &&
    destination.split(/[?#]/)[0] === '/dashboard/accounts-payable/bills'
}
export const rememberBillsNavigationEntry = (from: string, to: string) => {
  const fromPath = from.split(/[?#]/)[0]
  const toPath = to.split(/[?#]/)[0]
  nextEntrySource =
    toPath === '/dashboard/accounts-payable/bills' && fromPath !== toPath
      ? sidebarEntry
        ? 'sidebar'
        : fromPath === '/dashboard/accounts-payable'
        ? 'overview'
        : 'other_screen'
      : undefined
  sidebarEntry = false
  navigationHandoff = undefined
  try {
    const stored = sessionStorage.getItem(handoffKey)
    if (!stored || !current) return
    const handoff = JSON.parse(stored) as {
      destination: string
      attempt: BillsAttempt
      properties: Properties
    }
    const { destination, attempt, properties } = handoff
    if (
      attempt.accountId !== current.accountId ||
      attempt.screenVisitId !== current.screenVisitId ||
      Date.now() - attempt.startedAt > 30 * 60 * 1000
    )
      return
    const billPath = `/dashboard/accounts-payable/bills/${properties.billId}`
    const expected =
      destination === 'bill_create'
        ? '/dashboard/accounts-payable/bills/create'
        : destination === 'bill_edit'
        ? billPath
        : destination === 'bill_details'
        ? `${billPath}/details`
        : destination === 'vendor_details'
        ? `/dashboard/accounts-payable/vendors/${properties.vendorId}`
        : destination === 'payment_review' || destination === 'receiving_method'
        ? '/dashboard/accounts-payable/pay-one'
        : undefined
    if (expected === '/dashboard/accounts-payable/pay-one') {
      const query = new URL(to, 'https://localhost').searchParams
      const targetBillIds = (
        query.get('billIds') ??
        query.get('billId') ??
        ''
      ).split(',')
      const requestedBillIds = Array.isArray(properties.billIds)
        ? properties.billIds
        : [properties.billId]
      if (targetBillIds.sort().join(',') !== requestedBillIds.sort().join(','))
        return
    }
    if (toPath === expected)
      navigationHandoff = { target: to, attempt, properties }
  } catch {
    /* An unavailable handoff remains unknown. */
  }
}
export const billsNavigationFailed = (error: unknown, target: string) => {
  const handoff = navigationHandoff
  navigationHandoff = undefined
  if (
    !handoff ||
    handoff.target !== target ||
    (error &&
      typeof error === 'object' &&
      'cancelled' in error &&
      error.cancelled === true)
  )
    return
  billsAnalytics.error('navigation', error, handoff.properties, handoff.attempt)
}
export const takeBillsEntrySource = () => {
  const source = nextEntrySource
  nextEntrySource = undefined
  return source
}

let current: BillsContext | undefined
let inputMethod: BillsAttempt['inputMethod'] = 'pointer'
let currentAttempt: BillsAttempt | undefined
const preferences = new Map<string, BillsAttempt[]>()
const dedup = new Set<string>()
const fieldTimers = new Map<string, ReturnType<typeof setTimeout>>()
const errors = new WeakMap<object, { errorId: string; errorCode: string }>()
let lastAction: BillsEvent = 'screenViewed'
let query: BillsLoad | undefined
export const billsAnalytics = {
  context: () => current,
  setContext: (context: BillsContext | undefined) => {
    if (current?.screenVisitId !== context?.screenVisitId || !context)
      dedup.clear()
    current = context
    if (!context) {
      currentAttempt = undefined
      query = undefined
    }
  },
  setInputMethod: (method: BillsAttempt['inputMethod']) => {
    inputMethod = method
  },
  attempt: (): BillsAttempt | undefined =>
    current
      ? {
          ...current,
          sessionId: billsSessionId(),
          actionAttemptId: uuid(),
          operationId: uuid(),
          inputMethod,
          startedAt: Date.now()
        }
      : undefined,
  activeAttempt: () => currentAttempt,
  activate: (attempt: BillsAttempt | undefined) => {
    currentAttempt = attempt
  },
  emit: (
    event: BillsEvent,
    properties: Properties = {},
    context: BillsContext | BillsAttempt | BillsLoad | undefined = current,
    key?: string
  ) => {
    if (!context) return
    if (key && dedup.has(`${context.screenVisitId}:${key}`)) return
    try {
      const eventId = uuid()
      const safe = safeBillsProperties(
        [...billsEventFields[event].split(' '), ...commonFields],
        { ...context, ...properties }
      )
      const payload = {
        ...contextFields(context),
        ...safe,
        $session_id: billsSessionId(),
        eventId,
        $insert_id: eventId,
        schemaVersion: 1,
        timestamp: new Date().toISOString()
      }
      posthogManager.captureWhenReady(client => {
        try {
          client.capture(`apBills.${event}`, {
            ...payload,
            $session_id: payload.$session_id ?? client.get_session_id()
          })
        } catch {
          /* Optional analytics. */
        }
      })
      if (key) dedup.add(`${context.screenVisitId}:${key}`)
      lastAction = event
    } catch {
      /* Analytics must not affect product actions. */
    }
  },
  lastAction: () => lastAction,
  load: (context = current): BillsLoad | undefined => {
    if (!context) return undefined
    query = {
      ...context,
      loadAttemptId: uuid(),
      queryRevisionId: uuid(),
      startedAt: Date.now()
    }
    return query
  },
  currentLoad: () => query,
  takeLoad: (context = current): BillsLoad | undefined => {
    const load =
      query &&
      !query.claimed &&
      query.screenVisitId === context?.screenVisitId &&
      query.tab === context?.tab
        ? query
        : billsAnalytics.load(context)
    if (load) load.claimed = true
    return load
  },
  queryInteraction: (
    event:
      | 'searchCommitted'
      | 'filterApplied'
      | 'filterRemoved'
      | 'pageChanged'
      | 'pageSizeChanged',
    properties: Properties
  ) => {
    const attempt =
      event === 'filterApplied'
        ? currentAttempt ?? billsAnalytics.attempt()
        : billsAnalytics.attempt()
    const load = billsAnalytics.load(attempt)
    billsAnalytics.emit(
      event,
      {
        ...properties,
        loadAttemptId: load?.loadAttemptId,
        queryRevisionId: load?.queryRevisionId
      },
      attempt
    )
    return load
  },
  action: (properties: Properties) => {
    const attempt = billsAnalytics.attempt()
    currentAttempt = attempt
    billsAnalytics.emit('actionClicked', properties, attempt)
    return attempt
  },
  error: (
    operation: string,
    error: unknown,
    properties: Properties = {},
    attempt: BillsAttempt | BillsLoad | undefined = currentAttempt
  ) => {
    const existing =
      error && typeof error === 'object' ? errors.get(error) : undefined
    const errorId = existing?.errorId ?? uuid()
    const errorCode = existing?.errorCode ?? billsErrorCode(error)
    if (error && typeof error === 'object')
      errors.set(error, { errorId, errorCode })
    billsAnalytics.emit(
      'errorObserved',
      { ...properties, operation, errorId, errorCode },
      attempt ?? current,
      `error:${errorId}`
    )
    if (errorCode === 'network' || errorCode === 'timeout')
      billsAnalytics.emit(
        'attemptUnresolved',
        {
          ...properties,
          operation,
          reason: errorCode === 'timeout' ? 'timeout' : 'connection_lost',
          elapsedMs: attempt ? Date.now() - attempt.startedAt : 0
        },
        attempt
      )
    return { errorId, errorCode }
  },
  field: (properties: Properties, debounce = false) => {
    const attempt = currentAttempt ?? billsAnalytics.attempt()
    if (!debounce) {
      billsAnalytics.emit('filterFieldInteracted', properties, attempt)
      return
    }
    const key = `${attempt?.screenVisitId}:${properties.filterType}:${properties.field}`
    clearTimeout(fieldTimers.get(key))
    const safe = safeBillsProperties(
      billsEventFields.filterFieldInteracted.split(' '),
      properties
    )
    fieldTimers.set(
      key,
      setTimeout(() => {
        billsAnalytics.emit('filterFieldInteracted', safe, attempt)
        fieldTimers.delete(key)
      }, 300)
    )
  },
  preferenceChanged: (
    tableId: string,
    preference: 'columns' | 'page_size',
    properties: Properties
  ) => {
    const attempt = billsAnalytics.attempt()
    if (!attempt) return
    preferences.set(`${attempt.accountId}:${tableId}:${preference}`, [
      ...(preferences.get(`${attempt.accountId}:${tableId}:${preference}`) ??
        []),
      attempt
    ])
    billsAnalytics.emit(
      preference === 'columns' ? 'columnChanged' : 'pageSizeChanged',
      properties,
      attempt
    )
  },
  preferenceCompleted: (
    tableId: string,
    preference: 'columns' | 'page_size',
    accountId: string,
    outcome: 'saved' | 'failed',
    startedAt: number
  ) => {
    const key = `${accountId}:${tableId}:${preference}`
    const attempts = preferences.get(key) ?? []
    const completed = attempts.filter(attempt => attempt.startedAt <= startedAt)
    const remaining = attempts.filter(attempt => attempt.startedAt > startedAt)
    if (remaining.length) preferences.set(key, remaining)
    else preferences.delete(key)
    for (const attempt of completed)
      billsAnalytics.emit(
        'tablePreferenceSaved',
        {
          preference,
          outcome,
          durationMs: Date.now() - attempt.startedAt,
          ...(outcome === 'failed'
            ? { errorCode: 'persistence_failed', errorId: uuid() }
            : {})
        },
        attempt
      )
  }
}

const billsSessionId = () => {
  try {
    const id = posthogManager.getClientInstance()?.get_session_id()
    return isBillsId(id) ? id : undefined
  } catch {
    return undefined
  }
}
const contextFields = (context: BillsContext) => ({
  $user_agent:
    typeof navigator === 'undefined' ? undefined : navigator.userAgent,
  originSessionId: context.sessionId ?? billsSessionId(),
  screenId: context.screenId,
  screenVisitId: context.screenVisitId,
  originScreenVisitId: context.originScreenVisitId,
  userId: context.userId,
  accountId: context.accountId,
  partnerId: context.partnerId,
  tab: context.tab,
  environment: context.environment,
  trafficClass: context.trafficClass
})

export const billsRequestMetadata = (
  attempt: BillsAttempt | BillsLoad | undefined
): Record<string, string> => {
  if (!attempt || !isBillsId(attempt.originScreenVisitId)) return {}
  const metadata: Record<string, string> = {
    originScreenId: 'bills_list',
    originScreenVisitId: attempt.originScreenVisitId
  }
  if (attempt.trafficClass !== 'partner_self_service')
    metadata.trafficClass = attempt.trafficClass
  try {
    const sessionId =
      attempt.sessionId ?? posthogManager.getClientInstance()?.get_session_id()
    if (isBillsId(sessionId)) metadata.sessionId = sessionId
  } catch {
    /* Requests work when analytics is unavailable. */
  }
  if (
    'actionAttemptId' in attempt &&
    isBillsId(attempt.actionAttemptId) &&
    isBillsId(attempt.operationId)
  ) {
    metadata.actionAttemptId = attempt.actionAttemptId
    metadata.operationId = attempt.operationId
  }
  return metadata
}

const handoffKey = 'ap-bills-destination-v1'
export const rememberBillsDestination = (
  destination: string,
  properties: Properties,
  attempt: BillsAttempt | undefined
) => {
  if (!attempt) return
  try {
    sessionStorage.setItem(
      handoffKey,
      JSON.stringify({
        destination,
        attempt,
        properties: safeBillsProperties(
          ['billId', 'vendorId', 'billIds'],
          properties
        )
      })
    )
  } catch {
    /* Navigation still works. */
  }
}
export const trackBillsAction = (properties: Properties) => {
  const attempt = billsAnalytics.action(properties)
  if (
    typeof properties.destination === 'string' &&
    properties.destination !== 'confirmation'
  )
    rememberBillsDestination(properties.destination, properties, attempt)
  return attempt
}
export const billsDestinationOpened = (
  destination: string,
  accountId: string,
  properties: Properties = {}
) => {
  try {
    const stored = sessionStorage.getItem(handoffKey)
    if (!stored) return
    const handoff = JSON.parse(stored) as {
      destination: string
      attempt: BillsAttempt
      properties: Properties
    }
    if (
      handoff.destination !== destination ||
      handoff.attempt.accountId !== accountId ||
      Date.now() - handoff.attempt.startedAt > 30 * 60 * 1000
    )
      return
    if (properties.billId && properties.billId !== handoff.properties.billId)
      return
    if (
      properties.vendorId &&
      properties.vendorId !== handoff.properties.vendorId
    )
      return
    if (
      Array.isArray(properties.billIds) &&
      Array.isArray(handoff.properties.billIds) &&
      [...properties.billIds].sort().join(',') !==
        [...handoff.properties.billIds].sort().join(',')
    )
      return
    billsAnalytics.emit(
      'destinationOpened',
      {
        ...handoff.properties,
        destination,
        destinationVisitId: uuid(),
        durationMs: Date.now() - handoff.attempt.startedAt
      },
      handoff.attempt,
      `destination:${handoff.attempt.actionAttemptId}`
    )
    sessionStorage.removeItem(handoffKey)
  } catch {
    /* Missing observation is unknown, not failed navigation. */
  }
}

export const billsDestinationFailed = (
  destination: string,
  accountId: string,
  billIds: string[],
  error: unknown
) => {
  if (!billIds.length) return
  try {
    const stored = sessionStorage.getItem(handoffKey)
    if (!stored) return
    const handoff = JSON.parse(stored) as {
      destination: string
      attempt: BillsAttempt
      properties: Properties
    }
    const expectedBillIds = Array.isArray(handoff.properties.billIds)
      ? handoff.properties.billIds
      : handoff.properties.billId
      ? [handoff.properties.billId]
      : []
    if (
      handoff.destination !== destination ||
      handoff.attempt.accountId !== accountId ||
      Date.now() - handoff.attempt.startedAt > 30 * 60 * 1000 ||
      [...billIds].sort().join(',') !== [...expectedBillIds].sort().join(',')
    )
      return
    sessionStorage.removeItem(handoffKey)
    billsAnalytics.error(
      'navigation',
      error,
      handoff.properties,
      handoff.attempt
    )
  } catch {
    /* Entry still works when analytics or storage is unavailable. */
  }
}
